import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { BookService } from './services/bookService';
import { ImportBooksModal, ImportDlowModal } from './components';
import { getBookStatusColor, getBookStatusLabel } from './utils/bookStatus';

// Sort books by creation time, newest first.
// Prefer `created_at` (set fresh on every create, incl. clones); fall back to
// legacy date fields so older records without it still sort sensibly.
const sortByNewest = (list: any[]): any[] =>
  [...list].sort((a, b) => {
    const ta = new Date(a?.created_at || a?.createdDate || a?.lastModified || 0).getTime();
    const tb = new Date(b?.created_at || b?.createdDate || b?.lastModified || 0).getTime();
    return tb - ta;
  });

const BooksPage: React.FC = () => {
  const [books, setBooks] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statistics, setStatistics] = useState<any>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [isImportModalOpen, setIsImportModalOpen] = useState<boolean>(false);
  const [isImportDlowModalOpen, setIsImportDlowModalOpen] = useState<boolean>(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState<boolean>(false);
  const [bookToDelete, setBookToDelete] = useState<any>(null);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  // Fetch data from API
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch books (removed statistics and categories API calls)
        const booksResponse = await BookService.getBooks();

        // Handle books response
        let booksData: any[] = [];
        if (booksResponse) {
          booksData = booksResponse.data || booksResponse.result || booksResponse;
          setBooks(sortByNewest(Array.isArray(booksData) ? booksData : []));
        } else {
          console.error('Failed to fetch books');
          setBooks([]);
        }

        // Calculate statistics locally from books data
        const booksArray = Array.isArray(booksData) ? booksData : [];
        const calculatedStats = {
          total_books: booksArray.length,
          published_books: booksArray.filter((book: any) => book.status === 'Đã xuất bản' || book.status === 'published').length,
          draft_books: booksArray.filter((book: any) => book.status === 'Bản nháp' || book.status === 'draft').length
        };
        setStatistics(calculatedStats);

        setError(null);
      } catch (err) {
        console.error('Failed to fetch data:', err);
        setError('Không thể tải dữ liệu. Vui lòng thử lại.');
        setBooks([]);
        setStatistics(null);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Handle import success
  const handleImportSuccess = () => {
    // Refresh the books list after successful import
    const refreshData = async () => {
      try {
        setLoading(true);
        const booksResponse = await BookService.getBooks();

        if (booksResponse) {
          const booksData = booksResponse.data || booksResponse.result || booksResponse;
          const booksArray = Array.isArray(booksData) ? booksData : [];
          setBooks(sortByNewest(booksArray));

          // Update statistics locally
          const calculatedStats = {
            total_books: booksArray.length,
            published_books: booksArray.filter((book: any) => book.status === 'Đã xuất bản' || book.status === 'published').length,
            draft_books: booksArray.filter((book: any) => book.status === 'Bản nháp' || book.status === 'draft').length
          };
          setStatistics(calculatedStats);
        }
      } catch (err) {
        console.error('Failed to refresh data after import:', err);
      } finally {
        setLoading(false);
      }
    };

    refreshData();
  };

  // Handle delete book
  const handleDeleteBook = (book: any) => {
    setBookToDelete(book);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!bookToDelete) return;

    try {
      setIsDeleting(true);
      const response = await BookService.deleteBook(bookToDelete.id || bookToDelete._id);
      
      // Check if the deletion was successful
      if (response && response.success) {
        // Remove the book from the local state
        setBooks(prevBooks => 
          prevBooks.filter(book => (book.id || book._id) !== (bookToDelete.id || bookToDelete._id))
        );

        // Update statistics locally without API call
        setStatistics((prevStats: any) => ({
          ...prevStats,
          total_books: (prevStats?.total_books || 0) - 1,
          // Update other counts based on the deleted book's status
          published_books: bookToDelete.status === 'Đã xuất bản' || bookToDelete.status === 'published' 
            ? (prevStats?.published_books || 0) - 1 
            : prevStats?.published_books || 0,
          draft_books: bookToDelete.status === 'Bản nháp' || bookToDelete.status === 'draft' 
            ? (prevStats?.draft_books || 0) - 1 
            : prevStats?.draft_books || 0,
        }));

        setDeleteConfirmOpen(false);
        setBookToDelete(null);
        setError(null); // Clear any previous errors
      } else {
        // Handle unsuccessful deletion
        const errorMessage = response?.message || 'Không thể xóa sách. Vui lòng thử lại.';
        setError(errorMessage);
      }
    } catch (err) {
      console.error('Failed to delete book:', err);
      setError('Không thể xóa sách. Vui lòng thử lại.');
    } finally {
      setIsDeleting(false);
    }
  };

  const cancelDelete = () => {
    setDeleteConfirmOpen(false);
    setBookToDelete(null);
  };
  
  const filteredBooks = books.filter(book => {
    // Search filter only
    const searchMatch = !searchQuery || 
      (book.title || book.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (book.description || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (book.id || book._id || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (book.category || book.subject || '').toLowerCase().includes(searchQuery.toLowerCase());
    
    return searchMatch;
  });

  // Show loading state
  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Quản lý Sách</h1>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-8 text-center">
          <div className="animate-spin inline-block w-8 h-8 border-4 border-current border-t-transparent text-blue-600 rounded-full"></div>
          <p className="mt-4 text-gray-600">Đang tải danh sách sách...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Quản lý Sách</h1>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-8 text-center">
          <svg className="w-12 h-12 mx-auto text-red-500 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Có lỗi xảy ra</h3>
          <p className="text-gray-500 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
          >
            Tải lại trang
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Quản lý Sách</h1>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => setIsImportModalOpen(true)}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
          >
            <svg className="w-4 h-4 inline mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
            </svg>
            Import JSON
          </button>
          <button
            onClick={() => setIsImportDlowModalOpen(true)}
            className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
          >
            <svg className="w-4 h-4 inline mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
            Import Dlow
          </button>
          <Link
            to="/books/new"
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
          >
            <svg className="w-4 h-4 inline mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
            </svg>
            Tạo sách mới
          </Link>
        </div>
      </div>
      
      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow-sm p-6 border">
          <div className="flex items-center">
            <div className="bg-blue-100 p-3 rounded-lg">
              <svg className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Tổng sách</p>
              <p className="text-2xl font-semibold text-gray-900">
                {statistics?.total_books ?? books.length}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm p-6 border">
          <div className="flex items-center">
            <div className="bg-green-100 p-3 rounded-lg">
              <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Đã xuất bản</p>
              <p className="text-2xl font-semibold text-gray-900">
                {statistics?.published_books ?? books.filter(c => c.status?.toLowerCase() === 'published' || c.status === 'Đã xuất bản').length}
              </p>
            </div>
          </div>
        </div>




        <div className="bg-white rounded-lg shadow-sm p-6 border">
          <div className="flex items-center">
            <div className="bg-indigo-100 p-3 rounded-lg">
              <svg className="h-6 w-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Nháp</p>
              <p className="text-2xl font-semibold text-gray-900">
                {statistics?.draft_books ?? books.filter(c => c.status?.toLowerCase() === 'draft' || c.status === 'Nháp').length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Search Bar */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <input
            type="text"
            placeholder="Tìm kiếm sách theo tên, mô tả, ID hoặc danh mục..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute inset-y-0 right-0 pr-3 flex items-center"
            >
              <svg className="h-5 w-5 text-gray-400 hover:text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>

      
      {/* Books Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
        {filteredBooks.length === 0 ? (
          <div className="text-center py-12">
            <svg className="h-16 w-16 mx-auto mb-4 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Không tìm thấy sách</h3>
            <p className="text-gray-500">Thử thay đổi bộ lọc để xem thêm sách.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200" aria-label="Bảng danh sách sách">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Tên sách
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Danh mục
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Trạng thái
                  </th>
                  <th scope="col" className="relative px-6 py-3">
                    <span className="sr-only">Hành động</span>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredBooks.map((book) => (
                  <tr key={book.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 flex items-center justify-center rounded-md bg-blue-100 text-blue-700">
                          <svg
                            className="h-6 w-6"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            aria-hidden="true"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                            />
                          </svg>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            <Link 
                              to={`/books/${book.id || book._id}`}
                              className="hover:underline text-indigo-600"
                              aria-label={`Xem sách ${book.title || book.name}`}
                            >
                              {book.title || book.name || 'Không có tiêu đề'}
                            </Link>
                          </div>
                         
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        {book.category || book.subject || 'Chưa phân loại'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getBookStatusColor(book.status)}`}>
                        {getBookStatusLabel(book.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-3">
                        <Link
                          to={`/books/${book.id || book._id}`}
                          className="text-indigo-600 hover:text-indigo-900"
                          aria-label={`Xem sách ${book.title || book.name}`}
                        >
                          Xem chi tiết
                        </Link>
                        <button
                          onClick={() => handleDeleteBook(book)}
                          className="text-red-600 hover:text-red-900"
                          aria-label={`Xóa sách ${book.title || book.name}`}
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Import Modal */}
      <ImportBooksModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        onImportSuccess={handleImportSuccess}
      />

      {/* Import Dlow Modal */}
      <ImportDlowModal
        isOpen={isImportDlowModalOpen}
        onClose={() => setIsImportDlowModalOpen(false)}
        onImportSuccess={handleImportSuccess}
      />

      {/* Delete Confirmation Modal */}
      {deleteConfirmOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mt-4">Xác nhận xóa sách</h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500">
                  Bạn có chắc chắn muốn xóa sách <strong>"{bookToDelete?.title || bookToDelete?.name || 'Không có tiêu đề'}"</strong>?
                </p>
                <p className="text-sm text-red-600 mt-2">
                  Hành động này không thể hoàn tác.
                </p>
              </div>
              <div className="flex justify-center space-x-3 mt-4">
                <button
                  onClick={cancelDelete}
                  disabled={isDeleting}
                  className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded disabled:opacity-50"
                >
                  Hủy
                </button>
                <button
                  onClick={confirmDelete}
                  disabled={isDeleting}
                  className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded disabled:opacity-50 flex items-center"
                >
                  {isDeleting ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Đang xóa...
                    </>
                  ) : (
                    'Xóa sách'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BooksPage;
